/**
 * MixAnalyzer — Analyze tracks for mixing compatibility
 *
 * Heavy analysis (BPM, key, energy, spectral centroid) runs in a Web Worker
 * so the UI never freezes. Light methods (scoreCompatibility, suggestMixPoint)
 * stay on the main thread since they're already fast.
 */

export class MixAnalyzer {
  constructor(audioContext) {
    this.ctx = audioContext
    this._worker = null
    this._workerUrl = null
  }

  /**
   * Lazily create the Web Worker
   * Uses Vite's ?worker&url import pattern via dynamic URL
   */
  _getWorker() {
    if (this._worker) return this._worker

    // Create worker from the analysis-worker.js file
    this._workerUrl = new URL('./analysis-worker.js', import.meta.url)
    this._worker = new Worker(this._workerUrl, { type: 'module' })
    return this._worker
  }

  /**
   * Analyze track in a Web Worker — returns Promise
   * Handles BPM + key + energy + spectral centroid all at once
   *
   * @param {AudioBuffer} audioBuffer
   * @param {Function} [onProgress] - optional callback for progress updates
   * @returns {Promise<{bpm, key, energy, spectralCentroid}>}
   */
  analyzeTrack(audioBuffer, onProgress) {
    if (!audioBuffer) return Promise.resolve(null)

    return new Promise((resolve, reject) => {
      const worker = this._getWorker()

      // Copy channel data (transferable for zero-copy to worker)
      const channelData = audioBuffer.getChannelData(0)
      const copy = new Float32Array(channelData.length)
      copy.set(channelData)

      const handler = (e) => {
        const msg = e.data

        if (msg.type === 'progress' && onProgress) {
          onProgress(msg)
        } else if (msg.type === 'result') {
          worker.removeEventListener('message', handler)
          resolve({
            bpm: msg.bpm,
            key: msg.key,
            energy: msg.energy,
            spectralCentroid: msg.spectralCentroid
          })
        } else if (msg.type === 'error') {
          worker.removeEventListener('message', handler)
          reject(new Error(msg.message))
        }
      }

      worker.addEventListener('message', handler)

      // Transfer the buffer (zero-copy) so the worker gets it instantly
      worker.postMessage(
        { channelData: copy, sampleRate: audioBuffer.sampleRate },
        [copy.buffer]
      )
    })
  }

  /**
   * Score compatibility between two tracks (0-100)
   * Higher = better mix potential
   * Runs on main thread (lightweight math only)
   */
  scoreCompatibility(analysis1, analysis2, bpm1, bpm2) {
    if (!analysis1 || !analysis2) return 0

    let score = 0

    // BPM match: identical or close multiples (±5%)
    const bpmRatio = Math.max(bpm1, bpm2) / Math.min(bpm1, bpm2)
    const bpmScore = bpmRatio < 1.05 ? 30 : (bpmRatio < 1.1 ? 20 : 10)
    score += bpmScore

    // Key harmony: same key or relative minor/major (simplified)
    const keyScore = analysis1.key === analysis2.key ? 40 : 20
    score += keyScore

    // Energy match: similar loudness levels (within 20%)
    const energyDiff = Math.abs(analysis1.energy - analysis2.energy)
    const energyScore = energyDiff < 0.2 ? 30 : (energyDiff < 0.4 ? 15 : 0)
    score += energyScore

    return Math.min(100, score)
  }

  /**
   * Suggest optimal mix point in a track based on energy envelope
   * Returns time (seconds) where energy is decreasing = good transition moment
   * Runs on main thread (simple RMS scan — already fast)
   */
  suggestMixPoint(audioBuffer, duration) {
    if (!audioBuffer || !duration) return null

    const channelData = audioBuffer.getChannelData(0)
    const hopSize = 2048
    const energyEnvelope = []

    // Compute energy envelope
    for (let i = 0; i < channelData.length - hopSize; i += hopSize) {
      let sum = 0
      for (let j = i; j < i + hopSize; j++) {
        sum += channelData[j] * channelData[j]
      }
      const rms = Math.sqrt(sum / hopSize)
      energyEnvelope.push(rms)
    }

    // Find point where energy is decreasing (good for transition)
    const midpoint = Math.floor(energyEnvelope.length * 0.7)
    let bestTime = duration * 0.8
    let bestScore = Infinity

    for (let i = midpoint; i < energyEnvelope.length - 5; i++) {
      const energyDiff = energyEnvelope[i - 1] - energyEnvelope[i]
      const stability = Math.abs(energyEnvelope[i] - energyEnvelope[i + 1])
      const score = (energyDiff * 10) + (stability * 0.5)

      if (energyDiff > 0.001 && score < bestScore) {
        bestScore = score
        bestTime = (i * hopSize) / audioBuffer.sampleRate
      }
    }

    return Math.min(bestTime, duration * 0.95)
  }

  /**
   * Detect intro and outro boundaries using energy envelope analysis.
   * Runs on the main thread — it's a simple RMS scan, takes <10ms.
   *
   * Algorithm:
   *   1. Compute energy in 100ms hops across the full track
   *   2. introEnd   = first sustained rise above 20% of max energy (>2s sustained)
   *   3. outroStart = last sustained drop below 25% of max energy (>4s sustained)
   *
   * @param {AudioBuffer} audioBuffer
   * @returns {{ introEnd: number, outroStart: number }} times in seconds
   */
  detectIntroAndOutro(audioBuffer) {
    if (!audioBuffer) return { introEnd: 0, outroStart: audioBuffer?.duration ?? 0 }

    const channelData  = audioBuffer.getChannelData(0)
    const sampleRate   = audioBuffer.sampleRate
    const hopSize      = Math.floor(sampleRate * 0.1)   // 100ms resolution
    const energyEnv    = []

    // Build energy envelope
    for (let i = 0; i + hopSize < channelData.length; i += hopSize) {
      let sum = 0
      for (let j = i; j < i + hopSize; j++) sum += channelData[j] * channelData[j]
      energyEnv.push(Math.sqrt(sum / hopSize))
    }

    const maxEnergy      = Math.max(...energyEnv)
    if (maxEnergy === 0) return { introEnd: 0, outroStart: audioBuffer.duration }

    const introThresh    = maxEnergy * 0.20   // 20% of peak = "music started"
    const outroThresh    = maxEnergy * 0.25   // 25% of peak = "music ending"
    const sustainIntro   = Math.ceil(2.0 / 0.1)   // 20 hops = 2 seconds
    const sustainOutro   = Math.ceil(4.0 / 0.1)   // 40 hops = 4 seconds

    // ── Find intro end ──────────────────────────────────────────────────────
    let introEnd = 0
    for (let i = 0; i < energyEnv.length - sustainIntro; i++) {
      if (energyEnv[i] > introThresh) {
        // Check that energy stays above 70% of threshold for the sustain window
        const sustained = energyEnv
          .slice(i, i + sustainIntro)
          .every(e => e > introThresh * 0.7)
        if (sustained) {
          introEnd = (i * hopSize) / sampleRate
          break
        }
      }
    }

    // ── Find outro start ────────────────────────────────────────────────────
    // Search backwards from 85% through the track
    const searchFrom = Math.floor(energyEnv.length * 0.5)
    let outroStart   = audioBuffer.duration * 0.88   // safe default

    for (let i = energyEnv.length - sustainOutro - 1; i > searchFrom; i--) {
      if (energyEnv[i] < outroThresh) {
        const sustained = energyEnv
          .slice(i, i + sustainOutro)
          .every(e => e < outroThresh * 1.5)
        if (sustained) {
          outroStart = (i * hopSize) / sampleRate
          break
        }
      }
    }

    return { introEnd, outroStart }
  }

  /** Clean up the worker when done */
  dispose() {
    if (this._worker) {
      this._worker.terminate()
      this._worker = null
    }
  }
}
